// src/components/AddHoldingModal.jsx
import React, { useState, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { resolveFuturesUnderlying } from "../lib/futuresUnderlying";

const defaultForm = {
  ticker: "", name: "", market: "US", side: "Long", quantity: "", avgCost: "",
  currency: "USD", avgExchangeRate: "", notes: "",
};

export default function AddHoldingModal({ onAdd, onBulkAdd, onClose }) {
  const [tab, setTab] = useState("manual");
  const [form, setForm] = useState(defaultForm);
  const [preview, setPreview] = useState([]);
  const [parseError, setParseError] = useState("");
  const [replaceAll, setReplaceAll] = useState(true);   // 계좌 스냅샷 업로드는 기본 '전체 교체'
  const fileRef = useRef();

  const CURRENCY_MAP = { US: "USD", TW: "TWD", JP: "JPY", HK: "HKD", KR: "KRW" };

  const handleSubmit = () => {
    if (!form.ticker || !form.quantity || !form.avgCost) return;
    onAdd({
      ...form,
      quantity:        +form.quantity,
      avgCost:         +form.avgCost,
      avgExchangeRate: form.avgExchangeRate ? +form.avgExchangeRate : undefined,
    });
    setForm(defaultForm);
    onClose();
  };

  // 헤더/값 비교용: 소문자화 + 공백·언더스코어 제거 ("종목 코드" == "종목코드")
  const NORM = s => String(s ?? "").toLowerCase().replace(/[\s_]/g, "");
  const num = v => {
    const n = parseFloat(String(v ?? "").replace(/[, ]/g, ""));
    return Number.isFinite(n) ? n : 0;   // "#N/A" 등은 0
  };
  const hasCol = (row, key) => Object.keys(row).some(h => NORM(h) === NORM(key));
  const pick = (row, ...keys) => {
    for (const k of keys) {
      const h = Object.keys(row).find(x => NORM(x) === NORM(k));
      if (h && row[h] !== "" && row[h] != null) return row[h];
    }
    return "";
  };

  // aoa(행 배열) → 헤더 행 자동 탐지 후 {헤더명: 값} 객체 배열
  // (키움 원본은 앞쪽 빈 행·열이 있어 헤더가 1행에 없음)
  const aoaToObjects = aoa => {
    if (!aoa || !aoa.length) return [];
    let hi = aoa.findIndex(r => r && r.some(c => ["종목명", "종목코드", "ticker", "symbol"].includes(NORM(c))));
    if (hi < 0) hi = 0;
    const headers = (aoa[hi] || []).map(c => (c == null ? "" : String(c)));
    return aoa.slice(hi + 1)
      .filter(r => r && r.some(c => c != null && c !== ""))
      .map(r => {
        const o = {};
        headers.forEach((h, j) => { if (h) o[h] = r[j]; });
        return o;
      });
  };

  // 한 행 → holding. 시트 유형(키움 현물 롱 / 단선 숏 / 일반) 자동 판별.
  const rowToHolding = row => {
    // 1) 키움 단일주식선물 매도(숏): 잔고수량 + 구분
    if (hasCol(row, "잔고수량") && hasCol(row, "구분")) {
      const rawName = String(pick(row, "종목명")).replace(/\s*F\s*\d{6}.*$/i, "").trim(); // "현대차 F 202608 (10)" → "현대차"
      const u = resolveFuturesUnderlying(rawName);   // 선물 → 기초자산 코드/시장(라이브 시세용)
      const side = String(pick(row, "구분")).includes("매수") ? "Long" : "Short";
      return {
        ticker: u ? u.code : rawName,   // 기초자산 6자리코드(미매칭 시 종목명 폴백)
        name:   u ? u.name : rawName,
        market: u ? u.market : "KR",
        side,
        quantity: num(pick(row, "잔고수량")),   // 계약수 그대로(노미널 ×10은 노출계산에서 적용)
        avgCost:  num(pick(row, "평균단가")),    // 주당 평균단가
        currency: "KRW", avgExchangeRate: undefined, notes: "단선매도",
      };
    }
    // 2) 키움 현물 계좌잔고(롱): 금일 수량 + 장부단가 (순보유잔고는 대여/차입 반영돼 음수 나올 수 있음)
    if (hasCol(row, "순보유잔고")) {
      return {
        ticker: String(pick(row, "종목코드")).replace(/^A/i, "").trim(),  // "A003230" → "003230"
        name: String(pick(row, "종목명")),
        market: "KR", side: "Long",
        quantity: num(pick(row, "금일수량")),   // "금일 수량" 기준
        avgCost:  num(pick(row, "장부단가")),
        currency: "KRW", avgExchangeRate: undefined, notes: "",
      };
    }
    // 3) 일반 형식(기존 스키마 호환)
    const market = String(pick(row, "market", "마켓", "거래소")).toUpperCase() || "US";
    return {
      ticker: String(pick(row, "ticker", "symbol", "종목코드")).replace(/^A(?=\d{6}$)/i, "").toUpperCase(),
      name: String(pick(row, "name", "종목명")),
      market,
      side: String(pick(row, "side", "방향", "l/s")).toLowerCase().startsWith("s") ? "Short" : "Long",
      quantity: num(pick(row, "quantity", "qty", "수량")),
      avgCost:  num(pick(row, "avgcost", "avgprice", "평균단가", "장부원가", "cost", "price")),
      currency: String(pick(row, "currency", "통화")) || CURRENCY_MAP[market] || "USD",
      avgExchangeRate: num(pick(row, "avgexchangerate", "장부환율", "매수환율", "exchangerate")) || undefined,
      notes: String(pick(row, "notes", "메모")),
    };
  };

  const parseFile = file => {
    setParseError("");
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "csv") {
      Papa.parse(file, {
        header: false, skipEmptyLines: true,
        complete: res => processSheets([res.data]),
        error: err => setParseError(err.message),
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = e => {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        // 모든 시트를 aoa로 읽음 (Sheet1=롱, Sheet2=숏 등 다중 시트 지원)
        const sheets = wb.SheetNames.map(n =>
          XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, blankrows: false, defval: null })
        );
        processSheets(sheets);
      };
      reader.readAsBinaryString(file);
    } else {
      setParseError("CSV 또는 Excel 파일만 지원합니다.");
    }
  };

  const processSheets = sheetsAoa => {
    const rows = sheetsAoa.flatMap(aoaToObjects);
    const processed = rows.map(rowToHolding)
      .filter(r => r.ticker && r.quantity > 0 && r.avgCost > 0);
    if (!processed.length) {
      setParseError("유효한 데이터를 찾을 수 없습니다. 아래 형식을 확인해주세요.");
    } else {
      setPreview(processed);
    }
  };

  const inputStyle = { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #e8e8e8", fontSize: 13, boxSizing: "border-box" };
  const labelStyle = { fontSize: 12, color: "#888", display: "block", marginBottom: 4 };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 16, width: "min(580px, 95vw)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>종목 추가</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#aaa" }}>✕</button>
        </div>

        {/* 탭 */}
        <div style={{ padding: "0 24px", borderBottom: "1px solid #f0f0f0", display: "flex" }}>
          {["manual", "upload"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: "12px 20px", border: "none", background: "none", cursor: "pointer",
              fontSize: 13, fontWeight: tab === t ? 700 : 400,
              color: tab === t ? "#1a1a2e" : "#aaa",
              borderBottom: tab === t ? "2px solid #1a1a2e" : "2px solid transparent",
            }}>
              {t === "manual" ? "직접 입력" : "CSV/Excel 업로드"}
            </button>
          ))}
        </div>

        <div style={{ padding: 24 }}>
          {/* ── 직접 입력 탭 ── */}
          {tab === "manual" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>티커 *</label>
                  <input value={form.ticker} onChange={e => setForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
                    placeholder="예: AAPL, 3750, 7203" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>종목명</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="예: Apple Inc." style={inputStyle} />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>방향 (Long / Short) *</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {["Long", "Short"].map(s => (
                    <button key={s} type="button" onClick={() => setForm(f => ({ ...f, side: s }))}
                      style={{
                        flex: 1, padding: "9px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
                        border: form.side === s ? "none" : "1px solid #ddd",
                        background: form.side === s ? (s === "Short" ? "#c0392b" : "#0a7c59") : "#fff",
                        color: form.side === s ? "#fff" : "#888",
                      }}>
                      {s === "Short" ? "🔻 Short" : "🔺 Long"}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>마켓 *</label>
                  <select value={form.market} onChange={e => {
                    const mkt = e.target.value;
                    setForm(f => ({ ...f, market: mkt, currency: CURRENCY_MAP[mkt] || "USD" }));
                  }} style={{ ...inputStyle, background: "#fff" }}>
                    <option value="US">🇺🇸 US</option>
                    <option value="TW">🇹🇼 TW</option>
                    <option value="JP">🇯🇵 JP</option>
                    <option value="HK">🇭🇰 HK</option>
                    <option value="KR">🇰🇷 KR</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>수량 *</label>
                  <input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                    placeholder="100" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>통화</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    style={{ ...inputStyle, background: "#fff" }}>
                    <option value="USD">USD</option>
                    <option value="TWD">TWD</option>
                    <option value="JPY">JPY</option>
                    <option value="HKD">HKD</option>
                    <option value="KRW">KRW</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>장부원가 ({form.currency}) *</label>
                  <input type="number" value={form.avgCost} onChange={e => setForm(f => ({ ...f, avgCost: e.target.value }))}
                    placeholder="예: 175.50" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>
                    장부환율 (→KRW)
                    <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 4 }}>
                      {form.currency === "USD" ? "예: 1380" : form.currency === "JPY" ? "예: 9.2" : form.currency === "HKD" ? "예: 177" : form.currency === "TWD" ? "예: 43" : ""}
                    </span>
                  </label>
                  <input type="number" value={form.avgExchangeRate} onChange={e => setForm(f => ({ ...f, avgExchangeRate: e.target.value }))}
                    placeholder="매수 당시 환율" style={{ ...inputStyle, borderColor: form.avgExchangeRate ? "#e8e8e8" : "#f0a500" }} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>메모</label>
                <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="선택사항" style={inputStyle} />
              </div>

              {!form.avgExchangeRate && form.avgCost && (
                <div style={{ fontSize: 11, color: "#f0a500", background: "#fffbf0", padding: "8px 12px", borderRadius: 6, border: "1px solid #ffe0a0" }}>
                  ⚠️ 장부환율 미입력 시 현재 환율로 장부금액이 계산됩니다. 정확한 수익 계산을 위해 매수 당시 환율을 입력해주세요.
                </div>
              )}

              <button onClick={handleSubmit} disabled={!form.ticker || !form.quantity || !form.avgCost}
                style={{
                  padding: 10, borderRadius: 8, border: "none",
                  background: form.ticker && form.quantity && form.avgCost ? "#1a1a2e" : "#e8e8e8",
                  color: form.ticker && form.quantity && form.avgCost ? "#fff" : "#aaa",
                  fontSize: 14, fontWeight: 700, cursor: "pointer",
                }}>
                추가
              </button>
            </div>
          )}

          {/* ── CSV/Excel 업로드 탭 ── */}
          {tab === "upload" && (
            <div>
              <div style={{ background: "#f8f8f8", borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 12, color: "#555" }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>CSV/Excel 형식 안내</div>
                <div style={{ fontFamily: "monospace", fontSize: 11, lineHeight: 1.9 }}>
                  ticker, market, quantity, avgCost, currency, <strong>avgExchangeRate</strong>, name, notes<br/>
                  AAPL, US, 100, 175.5, USD, <strong>1380</strong>, Apple Inc.,<br/>
                  3750, HK, 27200, 71.28, HKD, <strong>177.5</strong>, CATL,<br/>
                  7203, JP, 200, 2800, JPY, <strong>9.2</strong>, Toyota,
                </div>
                <div style={{ marginTop: 8, color: "#aaa" }}>
                  * avgExchangeRate = 매수 당시 현지통화→KRW 환율 (선택, 없으면 현재 환율 사용)
                </div>
              </div>

              <div
                style={{ border: "2px dashed #e8e8e8", borderRadius: 8, padding: 32, textAlign: "center", cursor: "pointer", background: "#fafafa" }}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) parseFile(f); }}
              >
                <div style={{ fontSize: 24, marginBottom: 8 }}>📂</div>
                <div style={{ fontSize: 13, color: "#888" }}>클릭하거나 파일을 여기에 드래그하세요</div>
                <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>.csv, .xlsx, .xls 지원</div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
                  onChange={e => { if (e.target.files[0]) parseFile(e.target.files[0]); }} />
              </div>

              {parseError && (
                <div style={{ marginTop: 12, color: "#c0392b", fontSize: 12, padding: "8px 12px", background: "#fce4e4", borderRadius: 6 }}>
                  {parseError}
                </div>
              )}

              {preview.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>{preview.length}개 종목 인식됨</div>
                  <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: "#fafafa" }}>
                          {["ticker", "market", "방향", "수량", "장부원가", "통화", "장부환율"].map(h => (
                            <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: "#888", fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((r, i) => (
                          <tr key={i} style={{ borderTop: "1px solid #f8f8f8" }}>
                            <td style={{ padding: "6px 10px", fontWeight: 700 }}>{r.ticker}</td>
                            <td style={{ padding: "6px 10px" }}>{r.market}</td>
                            <td style={{ padding: "6px 10px", fontWeight: 700, color: r.side === "Short" ? "#c0392b" : "#0a7c59" }}>
                              {r.side === "Short" ? "🔻S" : "🔺L"}
                            </td>
                            <td style={{ padding: "6px 10px" }}>{r.quantity}</td>
                            <td style={{ padding: "6px 10px" }}>{r.avgCost}</td>
                            <td style={{ padding: "6px 10px" }}>{r.currency}</td>
                            <td style={{ padding: "6px 10px", color: r.avgExchangeRate ? "#1a1a2e" : "#f0a500" }}>
                              {r.avgExchangeRate || "미입력"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 12, color: replaceAll ? "#c0392b" : "#888", cursor: "pointer" }}>
                    <input type="checkbox" checked={replaceAll} onChange={e => setReplaceAll(e.target.checked)} />
                    기존 종목 전체 삭제 후 교체 <span style={{ color: "#aaa" }}>(끄면 기존에 추가·덮어쓰기)</span>
                  </label>
                  <button onClick={() => {
                      if (replaceAll && !window.confirm(`기존 종목을 모두 삭제하고 ${preview.length}개로 교체합니다. 계속할까요?`)) return;
                      onBulkAdd(preview, replaceAll);
                      onClose();
                    }}
                    style={{ marginTop: 10, width: "100%", padding: 10, borderRadius: 8, border: "none", background: replaceAll ? "#c0392b" : "#1a1a2e", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    {replaceAll ? `기존 전체 삭제 후 ${preview.length}개로 교체` : `${preview.length}개 종목 추가`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
